import { useEffect, useState } from 'react';
import {
  ensureAdobeIms,
  signInWithAdobe,
  signOutFromAdobe,
  syncAdobeImsSession
} from './adobeIms';
import { fetchUserRecord } from '../utils/database';

const SIGNED_OUT_SESSION: AdobeImsSession = {
  accessLevel: 'anonymous',
  accessToken: null,
  canManageData: false,
  isSignedIn: false,
  user: null
};

export interface AdobeImsAuthState extends AdobeImsSession {
  error: string | null;
  isLoading: boolean;
  isReady: boolean;
}

export function useAdobeImsAuth() {
  const [state, setState] = useState<AdobeImsAuthState>({
    ...SIGNED_OUT_SESSION,
    error: null,
    isLoading: true,
    isReady: false
  });

  // IMS initialisation — sets isSignedIn / accessToken / user from IMS callbacks.
  // Does NOT do the DB lookup (timing with IMS callbacks is unreliable).
  useEffect(() => {
    let isMounted = true;

    const setSession = (session: AdobeImsSession, opts: { error?: string | null } = {}) => {
      if (!isMounted) return;
      // Stay in loading while the DB lookup is still pending so the "not provisioned"
      // screen never flashes before fetchUserRecord resolves.
      const pendingDbLookup = session.isSignedIn && session.accessLevel === 'unregistered';
      setState({
        ...session,
        error: opts.error ?? null,
        isLoading: pendingDbLookup,
        isReady: true
      });
    };

    const bridge: MyAppAuthBridge = {
      onLogin: (session) => setSession(session),
      onLogout: () => setSession(SIGNED_OUT_SESSION),
      onAuthError: (error) => {
        if (!isMounted) return;
        setState((prev) => ({ ...prev, error: error.message, isLoading: false, isReady: true }));
      }
    };

    window.myAppAuth = bridge;

    void ensureAdobeIms()
      .then(() => syncAdobeImsSession())
      .then((session) => setSession(session))
      .catch((error: unknown) => {
        if (!isMounted) return;
        const message = error instanceof Error ? error.message : 'Unable to initialize Adobe IMS.';
        setState({ ...SIGNED_OUT_SESSION, error: message, isLoading: false, isReady: true });
      });

    return () => {
      isMounted = false;
      if (window.myAppAuth === bridge) delete window.myAppAuth;
    };
  }, []);

  // DB lookup — fires once IMS reports a signed-in session.
  // Runs after React state settles so the access token is guaranteed available.
  useEffect(() => {
    if (!state.isReady || !state.isSignedIn || !state.accessToken) return;
    if (state.accessLevel !== 'unregistered') return;

    let cancelled = false;
    fetchUserRecord(state.user?.email ?? '')
      .then((record) => {
        if (cancelled) return;
        if (!record) {
          setState((prev) => ({ ...prev, isLoading: false }));
          return;
        }
        setState((prev) => ({
          ...prev,
          accessLevel: record.accessLevel,
          canManageData: record.accessLevel === 'admin' || record.accessLevel === 'editor',
          isLoading: false
        }));
      })
      .catch(() => {
        if (!cancelled) setState((prev) => ({ ...prev, isLoading: false }));
      });

    return () => { cancelled = true; };
  }, [state.isReady, state.isSignedIn, state.accessToken, state.accessLevel]);

  const signIn = async () => {
    setState((prev) => ({ ...prev, error: null, isLoading: true }));
    try {
      await signInWithAdobe();
      setState((prev) => ({ ...prev, isLoading: false }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to start Adobe sign-in.';
      setState((prev) => ({ ...prev, error: message, isLoading: false, isReady: true }));
    }
  };

  const signOut = async () => {
    try {
      await signOutFromAdobe();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to sign out from Adobe IMS.';
      setState((prev) => ({ ...prev, error: message, isLoading: false, isReady: true }));
    }
  };

  return { ...state, signIn, signOut };
}
